import { ArrowRight, Building2, Landmark } from "lucide-react";
import type { CourtOfficer, CourtOfficerCourtType } from "./types";

interface CourtOfficerLandingProps {
  officers: CourtOfficer[];
  summary?: CourtOfficerSummary;
  onSelect: (courtType: CourtOfficerCourtType) => void;
}

export interface CourtOfficerSummary {
  total: number;
  highCourt: {
    total: number;
    judges: number;
    readers: number;
    controllers: number;
  };
  districtCourt: {
    total: number;
    judges: number;
    readers: number;
    controllers: number;
  };
  pendingAssignment?: number;
}

const countBy = (
  officers: CourtOfficer[],
  courtType: CourtOfficerCourtType,
  role?: string,
) =>
  officers.filter(
    (officer) =>
      officer.courtType === courtType && (!role || officer.role === role),
  ).length;

export default function CourtOfficerLanding({
  officers,
  summary,
  onSelect,
}: CourtOfficerLandingProps) {
  const highTotal =
    summary?.highCourt.total ?? countBy(officers, "high_courts");
  const districtTotal =
    summary?.districtCourt.total ?? countBy(officers, "district_courts");
  const highJudges =
    summary?.highCourt.judges ?? countBy(officers, "high_courts", "Judge");
  const highReaders =
    summary?.highCourt.readers ?? countBy(officers, "high_courts", "Reader");
  const highControllers =
    summary?.highCourt.controllers ??
    countBy(officers, "high_courts", "Controller");
  const districtJudges =
    summary?.districtCourt.judges ??
    countBy(officers, "district_courts", "Judge");
  const districtReaders =
    summary?.districtCourt.readers ??
    countBy(officers, "district_courts", "Reader");
  const districtControllers =
    summary?.districtCourt.controllers ??
    countBy(officers, "district_courts", "Controller");
  const pendingAssignment =
    summary?.pendingAssignment ??
    officers.filter(
      (officer) =>
        (officer.role === "Reader" || officer.role === "Controller") &&
        !officer.courtRoom &&
        !officer.benchRoom,
    ).length;
  const totalOfficers = summary?.total ?? highTotal + districtTotal;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-950">Court Officers</h1>
        <p className="mt-1 text-sm text-slate-500">
          Select the type of court to manage its officers
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <button
          type="button"
          onClick={() => onSelect("high_courts")}
          className="group min-h-[364px] rounded-[16px] border border-blue-200 bg-white p-9 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-[16px] bg-blue-50 text-blue-600">
            <Landmark size={30} />
          </div>
          <h2 className="text-xl font-bold text-slate-950">High Courts</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Manage Judges, Readers, and Controllers assigned to High Court
            benches and court halls.
          </p>
          <div className="mt-6 flex flex-wrap gap-5 text-sm text-slate-500">
            <span>{highJudges} Judges</span>
            <span>{highReaders} Readers</span>
            <span>{highControllers} Controllers</span>
          </div>
          <span className="mt-7 inline-flex items-center gap-2 rounded-[8px] bg-blue-600 px-6 py-3 text-sm font-semibold text-white">
            Manage Officers
            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </span>
        </button>

        <button
          type="button"
          onClick={() => onSelect("district_courts")}
          className="group min-h-[364px] rounded-[16px] border-2 border-[#f26422] bg-white p-9 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-[16px] bg-orange-50 text-orange-600">
            <Building2 size={30} />
          </div>
          <h2 className="text-xl font-bold text-slate-950">District Courts</h2>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Manage Judges, Readers, and Controllers assigned to District Court
            complexes and courtrooms.
          </p>
          <div className="mt-6 flex flex-wrap gap-5 text-sm text-slate-500">
            <span>{districtJudges} Judges</span>
            <span>{districtReaders} Readers</span>
            <span>{districtControllers} Controllers</span>
          </div>
          <span className="mt-7 inline-flex items-center gap-2 rounded-[8px] bg-[#f26422] px-6 py-3 text-sm font-semibold text-white">
            Manage Officers
            <ArrowRight
              size={17}
              className="transition group-hover:translate-x-1"
            />
          </span>
        </button>
      </div>

      <div className="grid overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm sm:grid-cols-4">
        <div className="flex items-center justify-center gap-3 border-b border-slate-100 p-7 sm:border-b-0 sm:border-r">
          <div>
            <div className="text-center text-3xl font-bold text-blue-700">
              {highTotal}
            </div>
            <div className="text-sm text-slate-500">High Court Officers</div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 border-b border-slate-100 p-7 sm:border-b-0 sm:border-r">
          <div>
            <div className="text-center text-3xl font-bold text-orange-600">
              {districtTotal}
            </div>
            <div className="text-sm text-slate-500">
              District Court Officers
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 border-b border-slate-100 p-7 sm:border-b-0 sm:border-r">
          <div>
            <div className="text-center text-3xl font-bold text-amber-600">
              {pendingAssignment}
            </div>
            <div className="text-sm text-slate-500">Pending Assignment</div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 p-7">
          <div>
            <div className="text-center text-3xl font-bold text-slate-950">
              {totalOfficers}
            </div>
            <div className="text-sm text-slate-500">Total Officers</div>
          </div>
        </div>
      </div>
    </div>
  );
}
